'use client';

import Link from 'next/link';
import { useCallback, useEffect, useState } from 'react';
import { brandConfig } from '@/config/brand';
import { useAuth } from '@/platform/auth/useAuth';
import { SitePageLayout } from '@/platform/layout/SitePageLayout';
import { SiteShell } from '@/platform/layout/SiteShell';
import { listGroups, requestGroupAccess, type Group } from './api';
import { visibilityLabel } from './visibilityLabel';

type Props = {
  /** Product tour: fixture groups, no API. */
  tourMode?: boolean;
  initialGroups?: Group[];
};

export function GroupDirectory({ tourMode = false, initialGroups }: Props) {
  const { user } = useAuth();
  const [groups, setGroups] = useState<Group[]>(initialGroups ?? []);
  const [error, setError] = useState<string | null>(null);
  const [requesting, setRequesting] = useState<string | null>(null);
  const [requested, setRequested] = useState<Record<string, boolean>>({});
  const Page = tourMode ? SitePageLayout : SiteShell;

  const reload = useCallback(() => {
    if (tourMode) {
      setGroups(initialGroups ?? []);
      return;
    }
    listGroups()
      .then((res) => setGroups(res.groups))
      .catch((err: Error) => setError(err.message));
  }, [tourMode, initialGroups]);

  useEffect(() => {
    reload();
  }, [reload]);

  async function onRequestAccess(groupId: string) {
    setRequesting(groupId);
    setError(null);
    try {
      await requestGroupAccess(groupId);
      setRequested((prev) => ({ ...prev, [groupId]: true }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Request failed');
    } finally {
      setRequesting(null);
    }
  }

  return (
    <Page
      title={brandConfig.groups.label}
      description={brandConfig.groups.description}
      actions={
        tourMode ? undefined : (
          <Link href="/groups/create/" className="btn btn-primary">
            Create group
          </Link>
        )
      }
      alerts={error ? <p className="form-error">{error}</p> : undefined}
    >
      <div className="social-feed" role="feed">
        {groups.length === 0 && !error ? (
          <div className="feed-card feed-card--empty">
            <p className="text-muted">No groups yet. Create the first community.</p>
            {!tourMode && (
              <Link href="/groups/create/" className="btn btn-primary">
                Create group
              </Link>
            )}
          </div>
        ) : (
          groups.map((g) => (
            <article key={g.id} className="feed-card">
              <h3 className="feed-card__title" style={{ margin: 0 }}>
                {tourMode ? g.name : <Link href={`/groups/${g.groupId}/`}>{g.name}</Link>}
              </h3>
              {g.description && <p className="feed-card__excerpt">{g.description}</p>}
              <footer className="feed-card__actions">
                <span className="feed-card__stat text-muted">
                  {g.memberCount} members · {visibilityLabel(g.visibility)}
                  {g.status === 'read_only' && ' · read-only'}
                </span>
                {!tourMode && (
                  <>
                    <Link href={`/groups/${g.groupId}/`} className="feed-card__cta">
                      Open group
                    </Link>
                    {user &&
                      g.visibility === 'private' &&
                      g.status !== 'read_only' &&
                      g.viewerMembershipStatus !== 'active' && (
                      <button
                        type="button"
                        className="btn btn-secondary"
                        disabled={
                          requesting === g.groupId ||
                          requested[g.groupId] ||
                          g.viewerMembershipStatus === 'pending'
                        }
                        onClick={() => void onRequestAccess(g.groupId)}
                      >
                        {requested[g.groupId] || g.viewerMembershipStatus === 'pending'
                          ? 'Membership pending'
                          : requesting === g.groupId
                            ? 'Requesting…'
                            : 'Request membership'}
                      </button>
                    )}
                  </>
                )}
              </footer>
            </article>
          ))
        )}
      </div>
    </Page>
  );
}
